import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { Cookies } from "react-cookie";
import { useStudioAgreement } from "@brandclub/bc-widgets";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
const COOKIE_PREFIX = "bc_concierge_agreement_";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const DEFAULT_VERSION = "v1";
const cookies = new Cookies();
function cookieName(appIdV2) {
  return `${COOKIE_PREFIX}${appIdV2 || "default"}`;
}
function setAcceptedCookie(appIdV2, version) {
  cookies.set(cookieName(appIdV2), version, {
    path: "/",
    maxAge: COOKIE_MAX_AGE,
    sameSite: "lax"
  });
}
async function postAcceptance({
  apiBaseUrl,
  appIdV2,
  version
}) {
  if (!apiBaseUrl) return;
  try {
    const headers = await getHeadersWithUniqueVisitorId();
    await fetch(`${apiBaseUrl}/api-rewards/concierge/agreement/accept`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ appIdV2, version })
    });
  } catch (err) {
    console.warn("[ConciergeAgreementGate] failed to record acceptance to DB", err);
  }
}
async function fetchAcceptanceStatus({
  apiBaseUrl,
  appIdV2,
  version
}) {
  if (!apiBaseUrl) return false;
  try {
    const headers = await getHeadersWithUniqueVisitorId();
    const url = `${apiBaseUrl}/api-rewards/concierge/agreement/status?appIdV2=${encodeURIComponent(
      appIdV2
    )}&version=${encodeURIComponent(version)}`;
    const res = await fetch(url, { method: "GET", headers });
    if (!res.ok) return false;
    const json = await res.json();
    return !!json.accepted;
  } catch (err) {
    console.warn("[ConciergeAgreementGate] failed to read acceptance from DB", err);
    return false;
  }
}
function useConciergeAgreementGate({
  appIdV2,
  apiBaseUrl,
  messages,
  onShown,
  onAccepted
}) {
  const agreement = useStudioAgreement();
  const [pending, setPending] = useState(null);
  const [serverAccepted, setServerAccepted] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const shownFiredRef = useRef(false);
  const version = (agreement == null ? void 0 : agreement.version) || DEFAULT_VERSION;
  const cookieAccepted = useMemo(() => {
    if (typeof window === "undefined") return false;
    const stored = cookies.get(cookieName(appIdV2));
    return typeof stored === "string" && stored === version;
  }, [appIdV2, version]);
  const hasAccepted = cookieAccepted || serverAccepted;
  const lookupKeyRef = useRef(null);
  useEffect(() => {
    const enabled = !!(agreement == null ? void 0 : agreement.enabled);
    if (!enabled || cookieAccepted || !appIdV2 || !apiBaseUrl) return;
    const lookupKey = `${appIdV2}#${version}`;
    if (lookupKeyRef.current === lookupKey) return;
    lookupKeyRef.current = lookupKey;
    let cancelled = false;
    void fetchAcceptanceStatus({ apiBaseUrl, appIdV2, version }).then((accepted) => {
      if (cancelled) return;
      if (accepted) {
        setAcceptedCookie(appIdV2, version);
        setServerAccepted(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [agreement == null ? void 0 : agreement.enabled, cookieAccepted, appIdV2, apiBaseUrl, version]);
  const missingIdWarnedRef = useRef(false);
  useEffect(() => {
    if (!!(agreement == null ? void 0 : agreement.enabled) && !appIdV2 && !missingIdWarnedRef.current) {
      missingIdWarnedRef.current = true;
      console.warn(
        "[ConciergeAgreementGate] agreement enabled but appIdV2 is missing — acceptance will be cookie-only (no DB persistence/recovery). Check that the brand-app row has appIdV2 backfilled."
      );
    }
  }, [agreement == null ? void 0 : agreement.enabled, appIdV2]);
  const isFirstTurn = useMemo(
    () => messages.every((m) => m.role !== "assistant"),
    [messages]
  );
  const isEnabled = !!(agreement == null ? void 0 : agreement.enabled);
  const shouldGate = isEnabled && !hasAccepted && isFirstTurn;
  const interceptSend = useCallback(
    (text, files) => {
      if (!shouldGate) return false;
      if ((agreement == null ? void 0 : agreement.template) === "auto-accept") {
        setAcceptedCookie(appIdV2, version);
        if (appIdV2) {
          void postAcceptance({ apiBaseUrl, appIdV2, version });
        }
        onAccepted == null ? void 0 : onAccepted();
        setShowInfoBanner(true);
        return false;
      }
      setPending({ text, files });
      return true;
    },
    [shouldGate, agreement == null ? void 0 : agreement.template, appIdV2, apiBaseUrl, version, onAccepted]
  );
  const accept = useCallback(() => {
    if (showInfoBanner) {
      setShowInfoBanner(false);
      shownFiredRef.current = false;
      return null;
    }
    setAcceptedCookie(appIdV2, version);
    if (appIdV2) {
      void postAcceptance({ apiBaseUrl, appIdV2, version });
    }
    const p = pending;
    setPending(null);
    shownFiredRef.current = false;
    onAccepted == null ? void 0 : onAccepted();
    return p;
  }, [appIdV2, apiBaseUrl, version, pending, showInfoBanner, onAccepted]);
  const reset = useCallback(() => {
    setPending(null);
    setShowInfoBanner(false);
    shownFiredRef.current = false;
  }, []);
  useEffect(() => {
    const visible = !!pending || showInfoBanner;
    if (visible && !shownFiredRef.current) {
      shownFiredRef.current = true;
      onShown == null ? void 0 : onShown();
    }
    if (!visible) {
      shownFiredRef.current = false;
    }
  }, [pending, showInfoBanner, onShown]);
  return {
    agreement,
    pending,
    shouldShowCard: !!pending || showInfoBanner,
    isEnabled,
    hasAccepted,
    interceptSend,
    accept,
    reset
  };
}
export {
  useConciergeAgreementGate
};
//# sourceMappingURL=useConciergeAgreementGate.js.map
